import type { DogFormData } from '../types';
import type { PetSnapshot } from '../types/pets';

// v3: la selección/distribución de recetas migró de estar keyed por Shopify
// variant id a estar keyed por product.id (UUID). Los drafts v2 estaban keyed
// por variant → son incompatibles con el nuevo contrato; subir la versión los
// invalida (descartarlos es aceptable) en vez de rehidratar claves obsoletas.
export const STORAGE_KEY = 'myl_form_state_v3';
// v2 en localStorage y en sessionStorage: drafts de la versión anterior. Ya no
// se migran (claves keyed por variant, incompatibles) — solo se limpian.
export const LEGACY_V2_LOCAL_KEY = 'myl_form_state_v2';
export const LEGACY_SESSION_KEY = 'myl_form_state_v2';
export const LEGACY_V1_KEY = 'myl_form_state';

export interface StoredDraft {
  formData: Partial<DogFormData>;
  step: number;
  lastUpdatedAt: number;
  petsCompleted?: PetSnapshot[];
  multiPetIntent?: boolean;
}

function safeParse(raw: string | null): StoredDraft | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.lastUpdatedAt !== 'number') {
      parsed.lastUpdatedAt = Date.now();
    }
    return parsed as StoredDraft;
  } catch {
    return null;
  }
}

// 90 días: alineado con la doc/spec. Un usuario que vuelve tras un email de
// recuperación dentro de la ventana recupera su plan ("Continuar el plan de X").
const MAX_DRAFT_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export function loadDraft(): StoredDraft | null {
  // Solo v3: los drafts v1/v2 estaban keyed por Shopify variant id y no son
  // compatibles con el nuevo contrato (product.id UUID). No se migran; se
  // limpian de forma oportunista para no dejar basura acumulada.
  const draft = safeParse(localStorage.getItem(STORAGE_KEY));
  if (draft) {
    if (Date.now() - draft.lastUpdatedAt > MAX_DRAFT_AGE_MS) {
      clearDraft();
      return null;
    }
    return draft;
  }

  clearLegacyDrafts();
  return null;
}

function clearLegacyDrafts(): void {
  try {
    localStorage.removeItem(LEGACY_V2_LOCAL_KEY);
  } catch {}
  try {
    localStorage.removeItem(LEGACY_V1_KEY);
  } catch {}
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {}
}

export function saveDraft(draft: Omit<StoredDraft, 'lastUpdatedAt'> | StoredDraft) {
  const payload: StoredDraft = {
    formData: draft.formData,
    step: draft.step,
    lastUpdatedAt: Date.now(),
    petsCompleted: draft.petsCompleted,
    multiPetIntent: draft.multiPetIntent,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {}
}

export function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  try {
    localStorage.removeItem(LEGACY_V2_LOCAL_KEY);
  } catch {}
  try {
    localStorage.removeItem(LEGACY_V1_KEY);
  } catch {}
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {}
}
